import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { currentWorkspaceId, setCurrentWorkspaceId } from "@/lib/api/workspaces";
import { useAuth } from "@/lib/auth/auth-context";

type WorkspaceValue = {
  /** The workspace every list, form and new record belongs to; null when signed out. */
  workspaceId: string | null;
  switchWorkspace: (id: string) => void;
};

const WorkspaceContext = createContext<WorkspaceValue | null>(null);

/**
 * Tracks the current workspace. Data queries are not keyed by workspace, so whenever it changes
 * (switching, joining, leaving, deleting) every cached query is reset and refetched.
 */
export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [version, setVersion] = useState(0);

  const workspaceId = useMemo(() => {
    void version; // re-read the stored choice after switchWorkspace()
    if (!user) return null;
    try {
      return currentWorkspaceId();
    } catch {
      return null;
    }
  }, [user, version]);

  const previous = useRef(workspaceId);
  useEffect(() => {
    if (previous.current !== workspaceId && previous.current !== null && workspaceId !== null) {
      void queryClient.resetQueries();
    }
    previous.current = workspaceId;
  }, [workspaceId, queryClient]);

  const switchWorkspace = useCallback((id: string) => {
    setCurrentWorkspaceId(id);
    setVersion((v) => v + 1);
  }, []);

  const value = useMemo(() => ({ workspaceId, switchWorkspace }), [workspaceId, switchWorkspace]);
  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useCurrentWorkspace(): WorkspaceValue {
  const value = useContext(WorkspaceContext);
  if (!value) throw new Error("useCurrentWorkspace must be used inside <WorkspaceProvider>");
  return value;
}
